import { MESSAGE_PROTOCOL_VERSION, bridgeMessageSchema } from "@ai-sales/contracts";
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "./api.js";
import { createClientId } from "./client-id.js";

const createNonce = () => `${createClientId()}-${Date.now()}`;

function trustedSalesforceParentOrigin(value: string | null): string | undefined {
  if (!value) return undefined;
  try {
    const url = new URL(value);
    const host = url.hostname.toLowerCase();
    const trusted = url.protocol === "https:" && (
      host.endsWith(".salesforce.com") || host.endsWith(".force.com") ||
      host.endsWith(".sfcrmproducts.cn") || host.endsWith(".sfcrmapps.cn")
    );
    return trusted ? url.origin : undefined;
  } catch {
    return undefined;
  }
}

export function useSalesforceAdminBridge() {
  const nonce = useMemo(createNonce, []);
  const parentOrigin = useMemo(() => trustedSalesforceParentOrigin(
    new URLSearchParams(window.location.search).get("parentOrigin")
  ), []);
  const embedded = Boolean(parentOrigin && window.parent !== window);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState("");

  const announce = useCallback(() => {
    if (!parentOrigin || window.parent === window) return;
    window.parent.postMessage({
      type: "agent.ready",
      version: MESSAGE_PROTOCOL_VERSION,
      requestId: createClientId(),
      nonce
    }, parentOrigin);
  }, [nonce, parentOrigin]);

  useEffect(() => {
    if (!embedded || !parentOrigin || connected) return;
    let exchangeStarted = false;
    const handler = (event: MessageEvent<unknown>) => {
      if (exchangeStarted || event.origin !== parentOrigin || event.source !== window.parent) return;
      const parsed = bridgeMessageSchema.safeParse(event.data);
      if (!parsed.success || parsed.data.nonce !== nonce || parsed.data.type !== "salesforce.session.ticket") return;
      exchangeStarted = true;
      setError("");
      void api.exchange(parsed.data.ticket, nonce, "admin")
        .then(() => setConnected(true))
        .catch((cause) => {
          setError(cause instanceof Error ? cause.message : "无法验证 Salesforce 管理员会话");
        });
    };
    window.addEventListener("message", handler);
    announce();
    const retryTimer = window.setInterval(() => { if (!exchangeStarted) announce(); }, 1_500);
    return () => {
      window.removeEventListener("message", handler);
      window.clearInterval(retryTimer);
    };
  }, [announce, connected, embedded, nonce, parentOrigin]);

  return { embedded, connected, error };
}
